// Thin client for the contacts Cloud Functions. One helper per backend
// endpoint, each accepting/returning the rich-shape Firestore contact
// (firstName/lastName/phones[]/emails[]/address/organization/...).
//
// Use these instead of `fetch(...api/add-contact)` so every form that
// touches contacts shares the same payload shape and error handling.
// The backend now writes rich fields to Google Contacts + Firestore in
// one call, so callers no longer need the old "POST basic, then
// updateContactInFirebase(rich)" two-step.

import { denormalizeContact, normalizeContact } from "./contactNormalize";
import { updateContactInFirebase } from "./firebasefunctions";

const API_BASE =
  "https://us-central1-team-edelstein.cloudfunctions.net/api";

// Build the full rich payload Humza's backend accepts.
function richPayload(contact) {
  return denormalizeContact(normalizeContact(contact));
}

/**
 * Create a contact in Google + Firestore.
 *
 * `contact` can be either the rich shape (firstName/lastName/phones[]/…)
 * or the legacy shape ({name, phone, email}). Both are normalized into
 * the rich shape before sending. The legacy fields are still included in
 * the payload so older Firestore consumers (deal customers list, etc.)
 * keep working.
 *
 * @returns { firebaseId, googleResourceName, ...rich }
 */
export async function addContact({ user, contact }) {
  if (!user?.id) throw new Error("user.id is required");
  const rich = richPayload(contact);

  const res = await fetch(`${API_BASE}/add-contact`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      // `token` is ignored by the backend (it loads refresh_token from
      // Firestore) — kept for backwards-compat during the rollout.
      token: user?.gmailtoken?.access_token,
      userId: user.id,
      ...rich,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || "Failed to add contact");
  }
  return {
    firebaseId: data.firebaseId,
    googleResourceName: data.googleResourceName || "",
    ...rich,
  };
}

/**
 * Update an existing contact in Google + Firestore.
 *
 * `contact` must include the Firestore doc id. `googleResourceName` is
 * optional — if missing, the backend will create the Google contact on
 * this call and return the new resourceName.
 *
 * `form` is the rich-shape edited payload. If omitted, `contact` itself
 * is used (useful when only `googleResourceName` changed).
 *
 * If the backend call fails (Gmail disconnected, network, etc.), falls
 * back to a Firestore-only patch so the user's edit isn't lost. Pass
 * `offlineFallback: false` to disable (callers who require Google sync,
 * like the auto-saving drawer, can opt out).
 *
 * @returns { googleResourceName, ...rich }
 */
export async function updateContact({
  user,
  contact,
  form,
  offlineFallback = true,
}) {
  if (!user?.id) throw new Error("user.id is required");
  if (!contact?.id) throw new Error("contact.id is required");
  const rich = richPayload(form || contact);

  try {
    const res = await fetch(`${API_BASE}/update-contact`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: user?.gmailtoken?.access_token,
        userId: user.id,
        contactId: contact.id,
        resourceName: contact.googleResourceName || "",
        ...rich,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.error || "Failed to update contact");
    }
    return {
      googleResourceName:
        data.googleResourceName || contact.googleResourceName || "",
      ...rich,
    };
  } catch (err) {
    if (!offlineFallback) throw err;
    // Backend unreachable or refused (e.g. user hasn't connected Gmail).
    // Patch Firestore directly so the change isn't lost; the next sync
    // will reconcile.
    console.warn(
      "Backend update failed, falling back to Firestore-only:",
      err?.message || err,
    );
    await updateContactInFirebase(contact.id, rich);
    return {
      googleResourceName: contact.googleResourceName || "",
      ...rich,
    };
  }
}
